import React, { useMemo, useState } from "react";
import {
  ReportDefinition,
  getReportGroupHeading,
  getReportListLabel,
  groupReportDefinitions,
} from "./reportDefinitions";

export type ReportSelectorProps = {
  definitions: readonly ReportDefinition[];
  disabled?: boolean;
  selectedReport: ReportDefinition | null;
  onChange: (report: ReportDefinition) => void;
};

const ReportSelector = ({
  definitions,
  disabled = false,
  selectedReport,
  onChange,
}: ReportSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const reportGroups = useMemo(
    () => groupReportDefinitions({ definitions, query: searchQuery }),
    [definitions, searchQuery],
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="relative">
        <svg
          aria-hidden="true"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9E9E9E"
          strokeWidth="2"
          strokeLinecap="round"
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
        >
          <circle cx="10.5" cy="10.5" r="7.5" />
          <path d="m16 16 5 5" />
        </svg>
        <input
          id="general-report-search"
          type="text"
          role="searchbox"
          aria-label="Search Reports"
          aria-controls="general-report-options"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search Reports"
          disabled={disabled}
          className="h-[43px] w-full rounded-lg border border-[#9E9E9E] bg-white py-[11px] pl-9 pr-3 text-[14px] leading-[19px] text-[#0A0A0A] outline-none placeholder:text-[#9E9E9E] focus-visible:border-cpaGold focus-visible:ring-2 focus-visible:ring-cpaGold/30 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <div
        id="general-report-options"
        role="group"
        aria-label="General reports"
        className="flex max-h-[1200px] flex-col gap-[14px] overflow-y-auto pr-1"
      >
        {reportGroups.length > 0 ? (
          reportGroups.map(({ group, reports }) => (
            <div key={group} className="flex flex-col gap-0.5">
              <h3 className="mb-[6px] mt-0 border-b-2 border-[#0A0A0A] px-[10px] pb-2 text-[12px] font-bold uppercase leading-4 tracking-[.05em] text-[#0A0A0A]">
                {getReportGroupHeading(group)}
              </h3>
              {reports.map((report) => {
                const selected = report.id === selectedReport?.id;

                return (
                  <button
                    key={report.id}
                    type="button"
                    aria-pressed={selected}
                    disabled={disabled}
                    onClick={() => onChange(report)}
                    className={`flex w-full items-center justify-between gap-[10px] rounded-lg border px-3 py-[10px] text-left text-[14px] leading-[1.35] text-[#0A0A0A] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold disabled:cursor-not-allowed disabled:opacity-50 ${
                      selected
                        ? "border-[#1878F2] bg-[#EFF6FE] font-bold"
                        : "border-transparent bg-transparent font-normal hover:bg-[#F7F7F8]"
                    }`}
                  >
                    <span className="min-w-0">{getReportListLabel(report)}</span>
                    {selected ? (
                      <svg
                        aria-hidden="true"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1878F2"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                      >
                        <path d="M5 13l4 4 10-10" />
                      </svg>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))
        ) : (
          <p
            className="m-0 px-[10px] py-3 text-[14px] leading-[19px] text-[#727272]"
            role="status"
            aria-live="polite"
          >
            No reports match your search.
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportSelector;
